import { Config } from "../../Config";
import { get } from "./api";
import { OpenDataResponse, OpenDataKpiReport, WaterProvider } from "./types";
import { getKpiReportFromOpenDataResult, KpiReport } from "../processors";
import _ from "lodash";
import { EMPTY_ARRAY } from "./constants";
import { fetchProviderByConnectionSizeCategories } from "./fetchProviderByConnectionSizeCategories";

export const queryKpisOpenDataByQueryString = async (
    query: string,
    providers?: WaterProvider[],
    options?: RequestInit
): Promise<KpiReport[]> => {
    if (query == null || query.trim() === "") return [];

    const kpis = await get<OpenDataResponse<{}, OpenDataKpiReport>>(
        `${Config.kpiOpenDataEndpoint}?sql=${query ?? ""}`,
        options
    );
    return getKpiReportFromOpenDataResult(providers, kpis?.result?.records ?? []);
};

export const queryKpisOpenData = async (
    params?: {
        kpis: string[][];
        providerNames?: string[];
        years?: number[];
    },
    providers?: WaterProvider[],
    options?: RequestInit,
    querySampleToDecideValidatity: boolean = true
): Promise<KpiReport[]> => {
    if (!params) return [];

    const { kpis, years, providerNames: _providerNames } = params;

    const providerNames = _.sortBy(_providerNames ?? [], d => d, "asc");

    const kpisQuery = _.join(
        _.orderBy(kpis ?? [], kpi => `${kpi[0]}###${kpi[1]}`, "asc")
            ?.map(kpi => {
                const kpiCode = kpi?.[0];
                const schemeType = kpi?.[1];
                return `(${_.join(
                    [
                        kpiCode == null || kpiCode.trim() === "" ? null : `"KPI Code" = '${kpiCode}'`,
                        schemeType == null || schemeType.trim() === "" ? null : `"Scheme Type" = '${schemeType}'`
                    ].filter(d => d != null),
                    " AND "
                )})`;
            })
            .filter(d => d != null && d.trim() !== "()") ?? [],
        " OR "
    );

    const providersQuery = _.join(
        providerNames
            ?.filter(name => name != null && name.trim() !== "")
            ?.map(d => {
                return `"Organisation" = '${d}'`;
            }) ?? [],
        " OR "
    );

    const yearsQuery = _.join(
        years?.map(d => {
            return `"Year" = '${d}'`;
        }) ?? [],
        " OR "
    );

    const condition = _.join(
        [kpisQuery, providersQuery, yearsQuery].filter(d => d != null && d.trim() !== "").map(d => `(${d})`),
        " AND "
    );

    const query =
        condition == null || condition.trim() === ""
            ? ""
            : `SELECT * from "${Config.kpiOpenDataId}" WHERE ${condition}`;

    let results = await queryKpisOpenDataByQueryString(query, providers, options);

    if (querySampleToDecideValidatity) {
        // query some sample providers (large) and decide if the data is accepted
        // only accepting data if data is really up-to-date, given the current latest year of large providers
        // const sampleProviderQuery = _.join(
        //     [
        //         "q=*:*",
        //         "rows=1",
        //         `fq=pure_strings_connection_size_ct:(large)`,
        //         //"fl=pure_strings_sname,pure_strings_admin_id,its_tid"
        //         `fl=${PROVIDER_FIELDS}`
        //     ],
        //     QUERY_URL_AND
        // );

        const extractedLargeProvider = providers?.find(d => d.connectionSizeCategory === "large");

        let sampleProviders: WaterProvider[] = null;
        if (extractedLargeProvider) {
            sampleProviders = [extractedLargeProvider];
        } else {
            // sampleProviders = await queryWaterProviderWithDocs(sampleProviderQuery);

            sampleProviders = await fetchProviderByConnectionSizeCategories(["large"], 1);
        }

        const sampleResults = await queryKpisOpenData(
            {
                providerNames: sampleProviders.map(d => d.kapiName),
                kpis
            },
            sampleProviders,
            {
                cache: "force-cache"
            },
            false
        );

        const year = _.max(sampleResults?.map(d => d.year)) - 2;
        results =
            sampleResults?.length > 0
                ? _.flattenDeep(
                      _.values(_.groupBy(results, d => d.organisation)).map(providerResult => {
                          const valid =
                              providerResult.filter(d => (d.value != null || d.valueStatus != null) && d.year >= year)
                                  .length > 0;

                          return valid ? providerResult : EMPTY_ARRAY;
                      })
                  )
                : results;
    }

    // if provider is QBWSA, kpi is water supplier length of mains return NR
    // if (
    //     providers?.length === 1 &&
    //     providers?.[0].adminId === QUEENSLAND_BULK_SUPPLIER &&
    //     kpis?.length === 1 &&
    //     kpis?.[0]?.[0] === WATER_SUPPLY_LENGTH_OF_MAINS_KPI_CODE
    // ) {
    //     return (
    //         results?.map(d => {
    //             return {
    //                 ...d,
    //                 value: null,
    //                 valueStatus: NA
    //             };
    //         }) ?? EMPTY_ARRAY
    //     );
    // }

    return results;
};
